import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import { useRatings } from '@/api/ratings';
import {
  FixedNav,
  Caption,
  Button,
  AnimatedNumber,
  PulseDot,
  SessionTimer,
  XpFloater,
  UnderstandingPrompt,
  KidHint,
  TurnIndicator,
  useMinDuration,
  ErrorMessageWithDebug,
  TACTICS_THEME_LABEL_FR,
  type TurnIndicatorState,
} from '@/ui';
import { useAwardXp } from '@/api/xp';
import type { ApiError } from '@/api/http';
import { classifySuccessOutcome, type FailOutcome } from '@/api/outcome';
import {
  tacticsApi,
  type NoMorePuzzlesError,
  type Puzzle,
  type TacticAttemptPayload,
  type TacticMode,
} from './api';
import { initialTacticsDrillState, tacticsDrillReducer } from './tacticsDrillReducer';
import { getInContextLine } from './inContextMapping';
import { TimePressedTimer } from './components/TimePressedTimer';
import { InContextIntro } from './components/InContextIntro';
import { MisereDecision, type MisereVerdict } from './components/MisereDecision';

/**
 * Détecte le 404 « no_more_puzzles » renvoyé par le backend quand
 * `offset >= count(theme)` (T9 MVP2.5). Le frontend doit afficher un
 * message kid-friendly plutôt qu'une erreur rouge alarmante.
 */
function isNoMorePuzzlesError(err: unknown): err is ApiError & { body: NoMorePuzzlesError } {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: unknown; body?: unknown };
  if (e.status !== 404) return false;
  const body = e.body as { error?: unknown } | undefined;
  return typeof body === 'object' && body !== null && body.error === 'no_more_puzzles';
}

/**
 * Tactics Dojo — drill (port V2 + refacto T2 MVP2.5).
 *
 * State machine migrée vers `useReducer` immutable (cf. `tacticsDrillReducer.ts`).
 * Le state ne porte plus d'instance `Chess` partagée — uniquement la FEN.
 * Le coup adversaire est dispatché via `useEffect(awaiting_engine)` +
 * `requestAnimationFrame` (résout le bug A2 tablette : `setTimeout` inline
 * était throttlé par iOS Safari pendant un touch event).
 *
 * Wiring inchangé : `useQuery(['tactics', 'list', theme])` + `POST /training/
 * attempts` avec `module=tactic, mode=tactic_drill`.
 */

/**
 * Source unique des labels FR des thèmes Lichess : `TACTICS_THEME_LABEL_FR`
 * exporté par `@/ui/labels.ts`. Toute nouvelle clé doit y être ajoutée pour
 * que les 3 surfaces consommatrices (TacticsDrillPage, TacticsListPage,
 * ParentDashboardPage) restent synchronisées.
 */

const THEME_SAMPLE: Record<string, string> = {
  deflection: 'Force le défenseur à quitter sa fonction',
  pin: 'Immobilise une pièce devant son roi',
  fork: 'Attaque 2 cibles simultanément',
  skewer: 'Attaque alignée, gain matériel',
  double_attack: 'Fourchette · attaque 2 cibles',
};

/**
 * Chantier 2 FFE — labels FR des 17 mats nommés du livre FFE Cavalier
 * (cf. `docs/pedagogie/audit-tactics-dojo-mats-ffe.md` §4). Seuls 4 sont
 * peuplés actuellement (Boden, Arabes, Couloir, Étouffée) ; les 13 autres
 * sont pré-réservés MVP4.2 (Concept Graph + re-seed CSV).
 */
const PATTERN_TAG_LABEL: Record<string, string> = {
  'mat-anastasie': "Mat d'Anastasie",
  'mat-arabes': 'Mat des Arabes',
  'mat-berger': 'Mat du Berger',
  'mat-blackburne': 'Mat de Blackburne',
  'mat-boden': 'Mat de Boden',
  'mat-couloir': 'Mat du Couloir',
  'mat-damiano': 'Mat de Damiano',
  'mat-debutant': 'Mat du Débutant',
  'mat-epaulettes': 'Mat des Épaulettes',
  'mat-escalier': "Mat de l'Escalier",
  'mat-etouffee': "Mat à l'Étouffée",
  'mat-gueridon': 'Mat du Guéridon',
  'mat-greco': 'Mat de Greco',
  'mat-lolli': 'Mat de Lolli',
  'mat-morphy': 'Mat de Morphy',
  'mat-pieces-lourdes': 'Mat des pièces lourdes',
  'mat-pillsbury': 'Mat de Pillsbury',
};

/**
 * Whitelist des modes Tactics Dojo lus depuis le query param `?mode=`.
 * Si la valeur n'est pas dans la liste, on fallback sur `tactic_drill` (mode
 * standard). Aucun mode = drill standard (rétro-compat URL existante).
 */
const ALLOWED_MODES: readonly TacticMode[] = [
  'tactic_drill',
  'tactic_drill_random',
  'tactic_drill_time_pressed',
  'tactic_drill_in_context',
  'tactic_drill_misere',
] as const;

function parseMode(raw: string | null): TacticMode {
  if (raw === null) return 'tactic_drill';
  return (ALLOWED_MODES as readonly string[]).includes(raw)
    ? (raw as TacticMode)
    : 'tactic_drill';
}

/** Durée du mode time-pressed, en millisecondes. Cf. MVP2.6 Gap 1. */
const TIME_PRESSED_DURATION_MS = 30000;

export function TacticsDrillPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = searchParams.get('theme') ?? undefined;
  const mode = parseMode(searchParams.get('mode'));
  // T4 P1 Stats Hub : source unifiée (cf. RATINGS_AUDIT §2.5).
  const ratings = useRatings();
  const lichessPuzzle = ratings.data?.lichess?.puzzle?.value ?? null;

  const listQ = useQuery({
    queryKey: ['tactics', 'list', theme],
    queryFn: () =>
      tacticsApi.list({ ...(theme ? { theme } : {}), limit: 20, random: true }),
  });

  const [state, dispatch] = useReducer(tacticsDrillReducer, initialTacticsDrillState);
  const [score, setScore] = useState<{ ok: number; ko: number }>({ ok: 0, ko: 0 });
  const [sessionXp, setSessionXp] = useState(0);
  const [xpBurst, setXpBurst] = useState<{ value: number; key: number } | null>(null);

  // ─── MVP2.6 Gap 2 — In-context : pré-animation de 10 coups d'ouverture ───
  // `inContextPhase` reflète si on est en train de jouer l'intro animée
  // (`'intro'`) ou si Léon a cliqué « À toi » (`'ready'`). Quand le mode
  // n'est pas in_context, reste figé sur `'ready'` (no-op).
  const [inContextPhase, setInContextPhase] = useState<'intro' | 'ready'>(
    mode === 'tactic_drill_in_context' ? 'intro' : 'ready',
  );
  // ─── MVP2.6 Gap 3 — Misère : tirage 50/50 leurre vs vraie tactique ───
  // `misereHasTactic` est tiré pour chaque puzzle (côté V0 — V1 lira
  // `puzzle.is_lure`). `misereVerdict` mémorise la décision de Léon pour
  // figer l'UI et router vers le drill ou la réfutation.
  const [misereHasTactic, setMisereHasTactic] = useState<boolean>(true);
  const [misereVerdict, setMisereVerdict] = useState<MisereVerdict | null>(null);
  // ─── MVP2.6 Gap 1 — Time-pressed : flag soumis à l'attempt + bool timeout ───
  // `timedOut` empêche un double-log si le timer expire pendant que Léon
  // est encore en `awaiting_player`.
  const timedOutRef = useRef<boolean>(false);
  const sessionStartRef = useRef<number>(Date.now());
  const positionStartRef = useRef<number>(Date.now());
  const attemptStartRef = useRef<number>(performance.now());
  // Évite d'awarder/scorer plusieurs fois le même puzzle si la transition
  // `awaiting_player` → `success` re-render plusieurs fois (StrictMode dev
  // double-render notamment). On marque le slug une fois traité.
  const awardedSuccessRef = useRef<string | null>(null);
  const recordedFailRef = useRef<string | null>(null);

  // Barème XP : +2 par puzzle réussi (drill court répétitif, cf. docs/XP_BAREME.md).
  const XP_PER_PUZZLE = 2;

  // T5 MVP2.5 : persiste l'XP côté backend (idempotence applicative 1h sur
  // (user_id, module, source_ref)). Le compteur local `sessionXp` reste
  // affiché pour le feedback immédiat (la mutation est fire-and-forget).
  const awardXp = useAwardXp();

  function startPuzzle(p: Puzzle): void {
    attemptStartRef.current = performance.now();
    positionStartRef.current = Date.now();
    awardedSuccessRef.current = null;
    recordedFailRef.current = null;
    timedOutRef.current = false;
    // MVP2.6 Gap 2 : si on est en mode in-context, on remet la phase à
    // 'intro' pour rejouer l'animation avant le prochain puzzle.
    if (mode === 'tactic_drill_in_context') {
      setInContextPhase('intro');
    }
    // MVP2.6 Gap 3 : tirage 50/50 leurre vs tactique pour le mode misère.
    // V0 : on tire au sort. V1 lira `p.is_lure` ou un flag YAML dédié.
    if (mode === 'tactic_drill_misere') {
      setMisereHasTactic(Math.random() >= 0.5);
      setMisereVerdict(null);
    }
    dispatch({ type: 'START_PUZZLE', puzzle: p });
  }

  useEffect(() => {
    if (listQ.data && listQ.data.puzzles.length > 0 && state.kind === 'idle') {
      startPuzzle(listQ.data.puzzles[0]!);
    }
    // `startPuzzle` est stable par construction (lit `mode` qui ne change
    // pas en cours de session — URL param). Inclure le dep créerait un
    // re-trigger inutile à chaque re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQ.data, state.kind]);

  // MVP2.6 Gap 2 — fallback si le thème n'a pas de mapping in-context.
  // On bascule la phase à `ready` pour éviter une UI bloquée sur un slot
  // d'intro vide. Le drill démarre normalement après.
  useEffect(() => {
    if (mode !== 'tactic_drill_in_context') return;
    if (inContextPhase !== 'intro') return;
    if (state.kind === 'idle') return;
    const line = getInContextLine(state.puzzle.theme);
    if (line === null) {
      setInContextPhase('ready');
    }
  }, [mode, inContextPhase, state]);

  // ─── Effet « coup adversaire » (T2 MVP2.5) ────────────────────────────
  // Sur transition `awaiting_engine` : on dispatch `MOVE_ENGINE` après une
  // frame `requestAnimationFrame` (~16 ms) suffisante pour laisser l'animation
  // 200 ms de Chessground commencer côté UI. On évite ainsi le bug A2 tablette
  // où un `setTimeout` inline était throttlé par iOS Safari pendant qu'un
  // touch event était consommé (le coup adversaire n'arrivait jamais).
  //
  // L'effet n'a pas besoin du contenu de `nextEngineMove` dans le deps array :
  // c'est `state.kind` qui pilote la transition (le reducer garantit que
  // dispatcher MOVE_ENGINE hors `awaiting_engine` est un no-op).
  useEffect(() => {
    if (state.kind !== 'awaiting_engine') return;
    const rafId = requestAnimationFrame(() => {
      dispatch({ type: 'MOVE_ENGINE' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [state.kind]);

  async function logAttempt(
    p: Puzzle,
    result: 'success' | 'fail',
    playedMove: string,
    expectedMove: string,
    timeSpentMs: number,
    outcome?: TacticAttemptPayload['outcome'],
    overrides?: { time_pressure?: boolean },
  ): Promise<void> {
    // MVP2.6 Gap 1 : on flag `time_pressure: true` pour toute tentative en
    // mode `tactic_drill_time_pressed`, indépendamment du résultat (succès
    // ou fail sous contrainte temporelle). Le backend (migration 020)
    // persiste dans `training_attempts.time_pressure` pour permettre
    // l'analyse parent « Léon échoue-t-il plus sous pression ? ».
    const timePressure =
      overrides?.time_pressure ?? mode === 'tactic_drill_time_pressed';
    const payload: TacticAttemptPayload = {
      module: 'tactic',
      slug: p.slug,
      kind: 'tactic_position',
      fen: p.fen,
      side_to_move: p.side_to_move,
      mode,
      result,
      played_move: playedMove,
      expected_move: expectedMove,
      time_spent_ms: timeSpentMs,
      source_ref: p.source.ref,
      ...(outcome ? { outcome } : {}),
      ...(timePressure ? { time_pressure: true } : {}),
    };
    try {
      await tacticsApi.postAttempt(payload);
    } catch (err) {
      console.error('[tactics] logAttempt failed', err);
    }
  }

  async function handleUnderstanding(understood: boolean): Promise<void> {
    if (state.kind !== 'fail' || state.outcomeLogged) return;
    const failOutcome: FailOutcome = understood ? 'fail_understood' : 'fail_not_understood';
    await logAttempt(
      state.puzzle,
      'fail',
      state.playedMove,
      state.expectedMove,
      state.timeSpentMs,
      failOutcome,
    );
    dispatch({ type: 'MARK_FAIL_LOGGED' });
  }

  function handleMove(move: BoardMoveEvent): void {
    // Le reducer fait toutes les vérifications (kind, legality, expectedSan).
    // On se contente de dispatcher la tentative + l'horloge.
    // F-4 P1 : `promotion` propagé si présent dans BoardMoveEvent (auto-promu
    // en dame par Board.detectAutoPromotion). Sans ce champ, chess.js v1
    // throw → coup ignoré silencieusement sur les puzzles avec promotion.
    dispatch({
      type: 'MOVE_PLAYER',
      from: move.from,
      to: move.to,
      ...(move.promotion ? { promotion: move.promotion } : {}),
      now: performance.now(),
      startedAt: attemptStartRef.current,
    });
  }

  /**
   * MVP2.6 Gap 1 — handler timeout du mode time-pressed. Déclenché par
   * `TimePressedTimer` quand `elapsed >= TIME_PRESSED_DURATION_MS`. On log
   * un fail synthétique côté backend avec `time_pressure: true` et on force
   * la transition `awaiting_player` → `fail` côté state machine pour que
   * Léon voie la solution attendue (au lieu de continuer à essayer).
   *
   * Idempotence via `timedOutRef` (StrictMode dev double-render).
   */
  function handleTimePressedTimeout(): void {
    if (timedOutRef.current) return;
    if (state.kind !== 'awaiting_player') return;
    timedOutRef.current = true;
    const expectedSan = state.puzzle.solution[state.cursor] ?? '';
    void logAttempt(
      state.puzzle,
      'fail',
      '(timeout)',
      expectedSan,
      TIME_PRESSED_DURATION_MS,
      'fail_not_understood',
      { time_pressure: true },
    );
    // On force la transition vers fail via une action MOVE_PLAYER « rate ».
    // Comme le reducer n'a pas d'action `TIMEOUT` dédiée, on simule un coup
    // illégal qui sera traité comme fail. Alternative : ajouter une action
    // dédiée au reducer — déféré V1 pour éviter de toucher la state machine
    // critique en MVP2.6 (rétro-compat des tests existants).
    // Plus simple : on dispatch un MOVE_PLAYER from=a1 to=a1 qui est illégal,
    // puis le composant n'ira pas en fail car la garde `playedSan === null`
    // retourne le state inchangé. Donc on utilise un mécanisme différent :
    // on enregistre côté state-locale `timedOut` pour basculer l'UI sans
    // toucher le reducer. Pour V0, on évite de toucher le reducer.
    // → l'UI lit `timedOutRef.current` et masque le board / affiche la
    //   solution dans un panneau dédié. Voir conditionnel render plus bas.
    // Force un re-render via setScore (effet de bord neutre + state change).
    setScore((s) => ({ ...s, ko: s.ko + 1 }));
  }

  /**
   * MVP2.6 Gap 3 — handler décision misère. Si `correct_positive` : on
   * laisse Léon jouer le drill (rien à faire, l'UI bascule sur le board
   * interactif via le state). Si `false_positive` : on affiche le panneau
   * de réfutation (rendu via `misereVerdict` dans le JSX). Pour les autres
   * verdicts, on log et on attend que Léon clique « Puzzle suivant ».
   */
  function handleMisereDecide(verdict: MisereVerdict): void {
    setMisereVerdict(verdict);
    // Pour V0 : pas d'écriture côté backend dédiée au verdict — l'outcome
    // sera enregistré au moment du logAttempt normal du drill (si Léon
    // continue) ou au passage au puzzle suivant (si fail_negative ou
    // correct_negative, qui sont des "non-events" côté training_attempts).
    // V1 : ajouter une colonne dédiée `misere_verdict` ou un outcome
    // étendu — hors scope MVP2.6.
  }

  // ─── Effets de bord post-transition (score, XP, log attempt) ──────────
  // On observe les transitions vers `success` / `fail` pour déclencher les
  // effets de bord (réseau, animation XP, score) UNE seule fois par puzzle.
  // Le reducer reste pur.
  useEffect(() => {
    if (state.kind === 'success' && awardedSuccessRef.current !== state.puzzle.slug) {
      awardedSuccessRef.current = state.puzzle.slug;
      const outcome = classifySuccessOutcome({ timeSpentMs: state.timeSpentMs });
      void logAttempt(
        state.puzzle,
        'success',
        state.playedMove,
        state.expectedMove,
        state.timeSpentMs,
        outcome,
      );
      setScore((s) => ({ ...s, ok: s.ok + 1 }));
      // Fix idempotence permanente 2026-05-17 : on n'affiche le XpFloater
      // et n'incrémente le sessionXp qu'APRÈS confirmation backend que
      // l'award est nouveau (`data.awarded === true`). Sinon Léon
      // rejoue un puzzle déjà résolu et voit +2 XP localement alors que
      // le backend skip silencieusement → divergence visuelle (le refresh
      // ferait disparaître les XP visuels). Backend idempotence permanente
      // sur (user, module, source_ref).
      awardXp.mutate(
        {
          module: 'tactic',
          source_ref: state.puzzle.slug,
          xp_amount: XP_PER_PUZZLE,
        },
        {
          onSuccess: (data) => {
            if (data.awarded) {
              setSessionXp((x) => x + XP_PER_PUZZLE);
              setXpBurst({ value: XP_PER_PUZZLE, key: Date.now() });
            }
          },
        },
      );
    } else if (state.kind === 'fail' && recordedFailRef.current !== state.puzzle.slug) {
      recordedFailRef.current = state.puzzle.slug;
      setScore((s) => ({ ...s, ko: s.ko + 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  const legalMoves = useMemo(() => {
    if (state.kind !== 'awaiting_player') return new Map<Key, Key[]>();
    const map = new Map<Key, Key[]>();
    const chess = new Chess(state.fen);
    for (const m of chess.moves({ verbose: true })) {
      const list = map.get(m.from as Key) ?? [];
      list.push(m.to as Key);
      map.set(m.from as Key, list);
    }
    return map;
  }, [state]);

  function nextPuzzle(): void {
    // T9.1 garde-fou : si Léon clique « suivant » sans répondre au prompt
    // d'échec, on log quand même l'attempt (outcome=null = legacy MVP1.x).
    // Sinon on perd la trace du fail dans `training_attempts` et le KPI
    // `recurring_mistake_rate` (T9.2) sous-estime les vraies erreurs.
    if (state.kind === 'fail' && !state.outcomeLogged) {
      void logAttempt(
        state.puzzle,
        'fail',
        state.playedMove,
        state.expectedMove,
        state.timeSpentMs,
        undefined,
      );
    }
    if (!listQ.data) return;
    const currentSlug =
      state.kind !== 'idle' ? state.puzzle.slug : null;
    const remaining = listQ.data.puzzles.filter((p) => p.slug !== currentSlug);
    if (remaining.length === 0) {
      void listQ.refetch();
      return;
    }
    startPuzzle(remaining[Math.floor(Math.random() * remaining.length)]!);
  }

  function backToThemes(): void {
    // T9.1 garde-fou : couvre le clic "Retour aux thèmes" — sans ça l'unmount
    // perdrait l'attempt si Léon n'a pas répondu au prompt.
    if (state.kind === 'fail' && !state.outcomeLogged) {
      void logAttempt(
        state.puzzle,
        'fail',
        state.playedMove,
        state.expectedMove,
        state.timeSpentMs,
        undefined,
      );
    }
    navigate('/tactics-dojo');
  }

  // ─── TurnIndicator (post-PR #98) ──────────────────────────────────────
  // Mapping reducer → state visuel. Le coup adversaire est rejoué via rAF
  // (cf. useEffect ci-dessus) donc `awaiting_engine` peut durer < 16ms.
  // `useMinDuration(rawState, 400)` force l'affichage minimum de 400ms pour
  // que Léon perçoive le clignotement « adversaire joue → à toi de jouer ».
  // Pattern reproduit depuis EndgameDrillPage (PR #98).
  const rawTurnState: TurnIndicatorState =
    state.kind === 'awaiting_player'
      ? 'user-turn'
      : state.kind === 'awaiting_engine'
        ? 'opponent-thinking'
        : 'idle';
  const turnState = useMinDuration<TurnIndicatorState>(rawTurnState, 400);

  const total = score.ok + score.ko;
  const successRate = total > 0 ? Math.round((score.ok / total) * 100) : 0;
  const currentPuzzle = state.kind !== 'idle' ? state.puzzle : null;
  // `inPlay` = la position est jouable ou en train d'animer le coup adversaire
  // (le board reste affiché dans les deux cas, seule l'interactivité change
  // via `engineThinking`).
  const inPlay =
    state.kind === 'awaiting_player' || state.kind === 'awaiting_engine';
  const themeMeta = currentPuzzle
    ? {
        id: currentPuzzle.theme,
        label: TACTICS_THEME_LABEL_FR[currentPuzzle.theme] ?? currentPuzzle.theme,
        sample: THEME_SAMPLE[currentPuzzle.theme],
      }
    : null;

  const chips = [
    { label: 'Thème', value: theme ? TACTICS_THEME_LABEL_FR[theme] ?? theme : 'tous', color: 'var(--cyan-500)' },
    { label: 'Réussite', value: `${successRate}%`, color: 'var(--volt-500)' },
  ];

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-6 flex flex-col gap-5">
        {/* Header condensé */}
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-body-sm">
            <Link
              to="/tactics-dojo"
              className="text-ink-300 hover:text-paper-50 transition-all duration-150"
            >
              ← Tactics Dojo
            </Link>
            <span className="font-mono text-[11px] text-ink-300">
              · {theme ?? 'tous thèmes'}
            </span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-heading-1 text-paper-50">
                {themeMeta?.label ?? 'Exercice tactique'}
              </h1>
              {currentPuzzle && (
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-cyan-500 text-cyan-500 text-[11px] font-mono">
                    <span className="size-1.5 rounded-full bg-cyan-500" aria-hidden="true" />
                    {themeMeta?.label}
                  </span>
                  <span className="font-mono text-[12px] text-ink-300">
                    rating <span className="text-paper-50">1900–2300</span>
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Session</Caption>
                <SessionTimer startedAtMs={sessionStartRef.current} />
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Réussite</Caption>
                <div className="flex items-center gap-1.5 font-mono text-sm">
                  <AnimatedNumber
                    value={successRate}
                    format={(v) => `${Math.round(v)}%`}
                    style={{
                      color: successRate >= 75 ? 'var(--volt-500)' : 'var(--amber-500)',
                      fontWeight: 500,
                    }}
                  />
                  <span className="text-ink-700">·</span>
                  <span className="text-volt-500">✓{score.ok}</span>
                  <span className="text-coral-500">✗{score.ko}</span>
                </div>
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>XP session</Caption>
                <span className="font-mono text-sm text-acid-500 font-medium tabular-nums">
                  +{sessionXp} pts
                </span>
              </div>
            </div>
          </div>
        </header>

        {mode === 'tactic_drill_time_pressed' && (
          <KidHint tone="cyan" title="Pourquoi ce drill ?">
            Tu travailles le motif tactique sous pression. L'objectif n'est pas
            juste de trouver la solution — c'est de la voir VITE pour pouvoir
            l'appliquer en partie blitz.
          </KidHint>
        )}

        {listQ.isPending && <p className="text-ink-300 italic">Chargement des puzzles…</p>}
        {listQ.isError && isNoMorePuzzlesError(listQ.error) && (
          <section
            data-testid="no-more-puzzles"
            className="glass glass-cyan p-6 rounded-lg flex flex-col gap-3 max-w-xl"
          >
            <Caption color="cyan">Bravo</Caption>
            <h2 className="font-display text-heading-2 text-paper-50">
              Tu as fini tous les puzzles de ce thème pour aujourd'hui.
            </h2>
            <p className="text-body-sm text-ink-300">
              Reviens demain ou choisis-en un autre.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                variant="primary"
                accent="cyan"
                onClick={() => navigate('/tactics-dojo')}
              >
                Choisir un autre thème
              </Button>
            </div>
          </section>
        )}
        {listQ.isError && !isNoMorePuzzlesError(listQ.error) && (
          <ErrorMessageWithDebug
            className="text-coral-500"
            message="Erreur de chargement. Réessaie dans quelques secondes."
            debug="GET /api/v1/content/tactics échoué"
          />
        )}
        {listQ.data && listQ.data.puzzles.length === 0 && (
          <p className="text-ink-300 italic">
            Aucun puzzle disponible pour ce thème.
          </p>
        )}

        {/* MVP2.6 Gap 2 — In-context : intro animée AVANT le drill. Si le
            thème du puzzle n'a pas de mapping (cf. inContextMapping.ts),
            on saute l'intro silencieusement (effet dédié plus haut). */}
        {currentPuzzle &&
          mode === 'tactic_drill_in_context' &&
          inContextPhase === 'intro' &&
          (() => {
            const line = getInContextLine(currentPuzzle.theme);
            if (line === null) return null;
            return (
              <InContextIntro
                line={line}
                orientation={currentPuzzle.side_to_move}
                onReady={() => setInContextPhase('ready')}
              />
            );
          })()}

        {currentPuzzle &&
          !(mode === 'tactic_drill_in_context' && inContextPhase === 'intro') && (
          <section className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-6 items-start">
            {/* LEFT — info & sequence */}
            <aside className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <DifficultyTile difficulty={currentPuzzle.difficulty} />
                <RatingDuo
                  puzzleRating="1900–2300"
                  leonRating={lichessPuzzle}
                />
              </div>

              {themeMeta && (
                <GlassCard accent="cyan" icon="🧠" label="Motif principal">
                  <h3 className="font-display text-heading-3 text-paper-50">
                    {themeMeta.label}
                  </h3>
                  {currentPuzzle.pattern_tag &&
                    PATTERN_TAG_LABEL[currentPuzzle.pattern_tag] && (
                      <p
                        className="mt-1 text-body-sm text-cyan-500 font-medium"
                        data-testid="pattern-tag-label"
                      >
                        {PATTERN_TAG_LABEL[currentPuzzle.pattern_tag]}
                        <span className="ml-2 text-ink-300 text-[11px] font-mono">
                          · FFE Cavalier
                        </span>
                      </p>
                    )}
                  {themeMeta.sample && (
                    <p className="mt-1.5 text-body-sm text-ink-300 italic leading-snug">
                      {themeMeta.sample}
                    </p>
                  )}
                  {currentPuzzle.secondary_themes.length > 0 && (
                    <div className="mt-3 flex gap-1.5 flex-wrap">
                      {currentPuzzle.secondary_themes.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center px-2 py-0.5 rounded-full border border-ink-700 bg-ink-900 font-mono text-[11px] text-ink-300"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </GlassCard>
              )}

              <GlassCard
                accent="violet"
                icon="➳"
                label={`Séquence · ${
                  state.kind !== 'idle' ? Math.floor(state.cursor / 2) + 1 : 1
                } / ${Math.ceil(currentPuzzle.solution.length / 2)}`}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  {currentPuzzle.solution.map((m, i) => {
                    const cursor = state.kind !== 'idle' ? state.cursor : 0;
                    // Pendant la résolution (awaiting_player / awaiting_engine)
                    // on n'affiche que les coups déjà joués ; en success/fail
                    // tout est révélé.
                    const revealed = !inPlay || i < cursor;
                    return (
                      <span key={i} className="contents">
                        {i > 0 && (
                          <span className="text-ink-300 font-mono text-xs">→</span>
                        )}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md border font-mono text-xs tabular-nums ${
                            revealed
                              ? 'border-cyan-500/40 bg-cyan-500/10 text-paper-50'
                              : 'border-ink-700 bg-ink-900 text-ink-300'
                          }`}
                        >
                          {revealed ? m : '?'}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </GlassCard>

              {(state.kind === 'success' || state.kind === 'fail') && (
                <GlassCard
                  accent={state.kind === 'success' ? 'volt' : 'coral'}
                  icon={state.kind === 'success' ? '✓' : '✗'}
                  label={
                    state.kind === 'success'
                      ? 'Résolu'
                      : `Raté — attendu ${state.expectedMove}`
                  }
                >
                  <p className="text-body-sm text-paper-50 leading-relaxed">
                    {currentPuzzle.explanation}
                  </p>
                  {state.kind === 'fail' && (
                    <>
                      <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-coral-500">
                        Carte de révision créée · due demain
                      </p>
                      <UnderstandingPrompt
                        done={state.outcomeLogged}
                        onAnswer={(understood) => void handleUnderstanding(understood)}
                      />
                    </>
                  )}
                </GlassCard>
              )}
            </aside>

            {/* RIGHT — board */}
            <div className="flex flex-col gap-3 relative">
              {xpBurst && (
                <XpFloater
                  value={xpBurst.value}
                  tokenKey={xpBurst.key}
                  caption="Puzzle résolu"
                  className="top-0 right-4"
                />
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <PulseDot
                    color={
                      state.kind === 'success'
                        ? 'var(--volt-500)'
                        : state.kind === 'fail'
                          ? 'var(--coral-500)'
                          : 'var(--cyan-500)'
                    }
                    size={7}
                  />
                  <Caption>
                    {state.kind === 'success'
                      ? 'Résolu'
                      : state.kind === 'fail'
                        ? 'Raté'
                        : state.kind === 'awaiting_engine'
                          ? 'Adversaire joue…'
                          : 'À toi'}
                  </Caption>
                </div>
                <PositionTimer
                  startedAtMs={positionStartRef.current}
                  active={state.kind === 'awaiting_player'}
                />
              </div>

              <TurnIndicator
                state={turnState}
                idleLabel={
                  state.kind === 'success'
                    ? 'Puzzle résolu'
                    : state.kind === 'fail'
                      ? 'Puzzle raté'
                      : 'En attente'
                }
              />

              {/* MVP2.6 Gap 3 — Misère : décision binaire AVANT de jouer.
                  Tant que `misereVerdict` est null, on cache l'interactivité
                  du board (via `inPlayBoard` plus bas) et on demande à Léon
                  de juger d'abord. */}
              {mode === 'tactic_drill_misere' && (
                <MisereDecision
                  key={currentPuzzle.slug}
                  hasTactic={misereHasTactic}
                  onDecide={handleMisereDecide}
                  {...(misereVerdict !== null ? { initialVerdict: misereVerdict } : {})}
                />
              )}

              {/* MVP2.6 Gap 1 — Time-pressed : timer 30s visible. Affiché
                  uniquement pendant `awaiting_player` (pas pendant le coup
                  adversaire ni en succès/fail). `key={slug}` pour reset le
                  compteur entre puzzles. */}
              {mode === 'tactic_drill_time_pressed' &&
                state.kind === 'awaiting_player' &&
                !timedOutRef.current && (
                  <TimePressedTimer
                    key={currentPuzzle.slug}
                    durationMs={TIME_PRESSED_DURATION_MS}
                    onTimeout={handleTimePressedTimeout}
                  />
                )}

              {/* MVP2.6 Gap 1 — Time-pressed timeout : panneau remplacement. */}
              {mode === 'tactic_drill_time_pressed' && timedOutRef.current && (
                <section
                  data-testid="time-pressed-timeout-panel"
                  className="flex flex-col gap-2 p-4 rounded-lg glass glass-coral"
                >
                  <Caption color="coral">Temps écoulé</Caption>
                  <p className="text-body-sm text-paper-50">
                    30 secondes écoulées. Le coup attendu était{' '}
                    <span className="font-mono text-coral-500">
                      {currentPuzzle.solution[0]}
                    </span>
                    .
                  </p>
                </section>
              )}

              {/* `key={currentPuzzle.slug}` force le démontage/remontage de
                  Chessground à chaque puzzle. Sans ça, l'instance Chessground
                  persiste entre puzzles et conserve son état interne
                  (`state.movable.dests` est wipé après chaque `userMove` —
                  cf. `chessground/board.js#baseUserMove`, `lastMove`
                  fantôme du puzzle précédent, animation en cours). En prod
                  (2026-05-17, Léon), ce cumul d'état après un fail
                  bloquait le 2ème coup du puzzle suivant : 1er coup OK,
                  engine répond, 2ème coup silencieux (drag refusé car
                  `dests` désynchronisé avec la nouvelle FEN).
                  Le slug est unique par puzzle (cf. `data/tactics/<theme>/`)
                  donc un nouveau puzzle = nouvelle instance Chessground. */}
              {/* MVP2.6 Gap 3 — Misère : board verrouillé tant que Léon
                  n'a pas pris sa décision OU s'il a faussement positif
                  (on lui montre la position, pas de coup à jouer).
                  MVP2.6 Gap 1 — Time-pressed : board verrouillé après
                  timeout (timedOutRef). */}
              <Board
                key={currentPuzzle.slug}
                fen={inPlay ? state.fen : currentPuzzle.fen}
                orientation={currentPuzzle.side_to_move}
                interactive={
                  state.kind === 'awaiting_player' &&
                  !timedOutRef.current &&
                  (mode !== 'tactic_drill_misere' || misereVerdict === 'correct_positive')
                }
                engineThinking={state.kind === 'awaiting_engine'}
                {...(state.kind === 'awaiting_player' &&
                !timedOutRef.current &&
                (mode !== 'tactic_drill_misere' || misereVerdict === 'correct_positive')
                  ? { legalMoves, turnColor: currentPuzzle.side_to_move }
                  : {})}
                {...(inPlay && state.lastMove ? { lastMove: state.lastMove } : {})}
                onMove={handleMove}
              />

              {/* Actions footer */}
              {state.kind === 'awaiting_player' && (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="ml-auto font-mono text-[11px] text-ink-300">
                    glisser · cliquer · clavier{' '}
                    <span className="px-1.5 py-0.5 rounded border border-ink-700 bg-ink-900 text-paper-50 ml-1">
                      f8b4 ↵
                    </span>
                  </span>
                </div>
              )}
              {(state.kind === 'success' || state.kind === 'fail') && (
                <div className="flex items-center gap-3 flex-wrap">
                  <Button variant="primary" accent="cyan" onClick={nextPuzzle}>
                    Puzzle suivant →
                  </Button>
                  <Button variant="ghost" onClick={backToThemes}>
                    Retour aux thèmes
                  </Button>
                  <span className="ml-auto font-mono text-[11px] text-ink-300">
                    Source ·{' '}
                    <a
                      href={`https://${currentPuzzle.source.ref}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-cyan-500 hover:underline underline-offset-4"
                    >
                      {currentPuzzle.source.ref}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function DifficultyTile({ difficulty }: { difficulty: number }): JSX.Element {
  const clamped = Math.max(1, Math.min(5, difficulty));
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Difficulté</Caption>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = n <= clamped;
          return (
            <span
              key={n}
              aria-hidden="true"
              className="w-3.5 h-3.5 rounded-sm"
              style={{
                backgroundColor: active ? 'var(--cyan-500)' : 'var(--ink-700)',
                boxShadow: active ? '0 0 6px -2px var(--cyan-500)' : 'none',
              }}
            />
          );
        })}
        <span className="ml-1.5 font-mono text-xs text-paper-50 tabular-nums">
          {clamped}/5
        </span>
      </div>
    </div>
  );
}

function RatingDuo({
  puzzleRating,
  leonRating,
}: {
  puzzleRating: string;
  leonRating: number | null;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Rating Lichess</Caption>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-mono text-paper-50 font-medium" style={{ fontSize: 18 }}>
          {puzzleRating}
        </span>
        {leonRating !== null && (
          <span className="font-mono text-[11px] text-ink-300">
            Léon: <span className="text-cyan-500">{leonRating}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function GlassCard({
  accent,
  icon,
  label,
  children,
}: {
  accent: 'cyan' | 'violet' | 'volt' | 'coral';
  icon: string;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  const accentColor = {
    cyan: 'var(--cyan-500)',
    violet: 'var(--violet-500)',
    volt: 'var(--volt-500)',
    coral: 'var(--coral-500)',
  }[accent];
  // String littéral (pas template) : Tailwind purge supprime les classes
  // construites via `glass-${accent}` faute de pouvoir les détecter.
  const glassClass = {
    cyan: 'glass glass-cyan',
    violet: 'glass glass-violet',
    volt: 'glass glass-volt',
    coral: 'glass glass-coral',
  }[accent];
  return (
    <div className={`relative p-4 rounded-lg ${glassClass}`}>
      <span
        aria-hidden="true"
        className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded border font-mono text-xs"
        style={{ color: accentColor, borderColor: `${accentColor}33`, backgroundColor: `${accentColor}10` }}
      >
        {icon}
      </span>
      <div className="mr-9">
        <span
          className="block text-caption uppercase mb-2"
          style={{ color: accentColor, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {label}
        </span>
        {children}
      </div>
    </div>
  );
}

function PositionTimer({
  startedAtMs,
  active,
}: {
  startedAtMs: number;
  active: boolean;
}): JSX.Element {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [active]);
  const elapsed = Math.max(0, (now - startedAtMs) / 1000);
  return (
    <span
      className="font-mono text-[11px] tabular-nums whitespace-nowrap"
      style={{ color: elapsed > 30 ? 'var(--amber-500)' : 'var(--ink-300)' }}
    >
      {elapsed.toFixed(1)}s
    </span>
  );
}
